import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles blur on the quantity input. Detect typed-below-min so we can
   * surface a minimum-order error before delegating to the parent's clamping
   * setQuantity logic.
   *
   * @param {Event} event
   */
  setQuantity(event) {
    if (event.target instanceof HTMLInputElement) {
      const { min } = this.getCurrentValues();
      const typed = parseInt(event.target.value);
      if (Number.isFinite(typed) && typed < min) {
        this.#showMinError(min);
      } else {
        this.#hideMinError();
      }
    }
    super.setQuantity(event);
  }

  /**
   * Resolves the line-item error refs sitting on the parent cart-items
   * component for this quantity selector's row.
   *
   * @returns {{ container: HTMLElement, text: HTMLElement } | null}
   */
  #getErrorRefs() {
    const input = this.refs.quantityInput;
    const line = input?.dataset.cartLine;
    if (!line) return null;

    const cartItems = this.closest('cart-items-component');
    if (!cartItems) return null;

    const container = /** @type {any} */ (cartItems).refs?.[`cartItemErrorContainer-${line}`];
    const text = /** @type {any} */ (cartItems).refs?.[`cartItemError-${line}`];
    if (!(container instanceof HTMLElement) || !(text instanceof HTMLElement)) return null;
    return { container, text };
  }

  /**
   * @param {number} min
   */
  #showMinError(min) {
    const refs = this.#getErrorRefs();
    if (!refs) return;
    refs.text.textContent = `Minimum order is ${min}.`;
    refs.container.classList.remove('hidden');
  }

  #hideMinError() {
    const refs = this.#getErrorRefs();
    if (!refs) return;
    if (refs.text.textContent?.startsWith('Minimum order')) {
      refs.text.textContent = '';
      refs.container.classList.add('hidden');
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
